import { api } from '@/lib/api';
import { build_params, download_export } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type {
  ListFilters,
  PayoutSecret,
  VendorPayout,
  PresignedUrlResponse,
  SchedulingConfig,
  StatusChangeResponse,
  UpdateSchedulingRequest,
  VendorDetail,
  VendorListResponse,
  ServiceVisibilityInput,
  ServiceVisibilityResult,
} from './types';

/**
 * `build_params` drops empty values and folds the sort token into the two
 * params the API takes, so a filter can no longer be wired into the query key
 * (making the screen refetch) while being forgotten in the request.
 */
export async function fetch_vendor_list(
  filters: ListFilters,
  sort: SortState = null,
): Promise<VendorListResponse> {
  const res = await api.get<VendorListResponse>('/admin/vendors', {
    params: build_params(filters as Record<string, string | number | undefined>, sort),
  });
  return res.data;
}

/** Every vendor matching the current filters, as a CSV. Paging is excluded. */
export function export_vendors(filters: ListFilters, sort: SortState): Promise<void> {
  const { page: _page, page_size: _page_size, ...rest } = filters;
  return download_export(
    '/admin/vendors/export',
    build_params(rest as Record<string, string | number | undefined>, sort),
    'vendors.csv',
  );
}

export async function fetch_vendor_detail(id: string): Promise<VendorDetail> {
  const res = await api.get<VendorDetail>(`/admin/vendors/${id}`);
  return res.data;
}

export async function approve_vendor(id: string): Promise<StatusChangeResponse> {
  const res = await api.post<StatusChangeResponse>(`/admin/vendors/${id}/approve`);
  return res.data;
}

export async function reject_vendor(id: string, reason: string): Promise<StatusChangeResponse> {
  const res = await api.post<StatusChangeResponse>(`/admin/vendors/${id}/reject`, { reason });
  return res.data;
}

export async function suspend_vendor(id: string, reason: string): Promise<StatusChangeResponse> {
  const res = await api.post<StatusChangeResponse>(`/admin/vendors/${id}/suspend`, { reason });
  return res.data;
}

/**
 * Payout input for a vendor that has none. Create-only on the server: changing
 * an existing destination stays with the vendor, in their own app.
 */
export type PayoutInput =
  | {
      method: 'BANK_ACCOUNT';
      account_holder_name: string;
      account_number: string;
      ifsc_code: string;
    }
  | { method: 'UPI'; upi_id: string };

export async function set_vendor_payout(id: string, input: PayoutInput): Promise<VendorPayout> {
  const res = await api.post<VendorPayout>(`/admin/vendors/${id}/payout`, input);
  return res.data;
}

/**
 * Hide or restore one of a vendor's services.
 *
 * Moderation of a single catalog line, so a mispriced or unpublishable offering
 * does not mean suspending the whole business. The reason is mandatory and is
 * repeated to the vendor.
 */
export async function set_service_visibility(
  vendor_id: string,
  service_id: string,
  input: ServiceVisibilityInput,
): Promise<ServiceVisibilityResult> {
  const res = await api.post<ServiceVisibilityResult>(
    `/admin/vendors/${vendor_id}/services/${service_id}/visibility`,
    input,
  );
  return res.data;
}

/** What the server reports back after asking a vendor for payout details. */
export type PayoutRequestResult = {
  requested_at: string;
  requested_by_name: string | null;
};

/**
 * Ask the vendor to add their own payout details.
 *
 * The ordinary path — `set_vendor_payout` is the exception for when the vendor
 * cannot or will not do it themselves.
 */
export async function request_vendor_payout(id: string): Promise<PayoutRequestResult> {
  const res = await api.post<PayoutRequestResult>(`/admin/vendors/${id}/payout/request`);
  return res.data;
}

/**
 * The vendor's real payout destination, for an admin who has to actually pay them.
 *
 * POST, not GET: every call is audited server-side, so this must never be something
 * a prefetch or a cache warmer can trigger. The result goes straight to the
 * clipboard and is never held in component state — see `PayoutCard`.
 */
export async function reveal_vendor_payout(id: string): Promise<PayoutSecret> {
  const res = await api.post<PayoutSecret>(`/admin/vendors/${id}/payout/reveal`);
  return res.data;
}

/**
 * Tick or untick one KYC document.
 *
 * Does not touch `kyc_status` — approval stays vendor-level. This answers the
 * separate question the console asked on every document row and could never
 * settle: has anyone actually read this one.
 */
export async function verify_vendor_document(
  vendor_id: string,
  doc_id: string,
  verified: boolean,
): Promise<{ id: string; verified_at: string | null; verified_by_name: string | null }> {
  const res = await api.post<{ id: string; verified_at: string | null; verified_by_name: string | null }>(
    `/admin/vendors/${vendor_id}/documents/${doc_id}/verify`,
    { verified },
  );
  return res.data;
}

export async function update_scheduling(
  id: string,
  input: UpdateSchedulingRequest,
): Promise<SchedulingConfig> {
  const res = await api.patch<SchedulingConfig>(`/admin/vendors/${id}/scheduling`, input);
  return res.data;
}

export async function fetch_document_presigned_url(
  vendor_id: string,
  doc_id: string,
): Promise<PresignedUrlResponse> {
  const res = await api.get<PresignedUrlResponse>(
    `/admin/vendors/${vendor_id}/documents/${doc_id}/presigned-url`,
  );
  return res.data;
}

export type PhotoVariant = 'thumb' | 'medium' | 'full';

/** Path builders for the streaming file endpoints (consumed via AuthImage / blob fetch). */
export function document_file_path(vendor_id: string, doc_id: string): string {
  return `/admin/vendors/${vendor_id}/documents/${doc_id}/file`;
}

export function photo_file_path(
  vendor_id: string,
  photo_id: string,
  variant: PhotoVariant = 'thumb',
): string {
  return `/admin/vendors/${vendor_id}/photos/${photo_id}/file?variant=${variant}`;
}
